import { LuIcon, Text } from "@overmap-ai/blocks"
import { type ProcedureType, type Stored, toUuidArray, EMPTY_ARRAY } from "@overmap-ai/common"
import { use, useCallback, useMemo } from "react"

import { ProcedureTypeMultiSelect } from "../../../components/DropdownMultiSelects/ProcedureTypeMultiSelect"
import { ProcedureTypeDropdownSelect } from "../../../components/DropdownSelects/ProcedureTypeDropdownSelect"
import { ProcedureTypeIcon, ProcedureTypeIcons } from "../../../components/Icons/ProcedureTypeIcon"
import { modelIconMapping } from "../../../mappings/icons"
import { FilterModifierDropdown, RemoveFilterButton } from "../../components"
import { FilterCellBadge } from "../../components/FilterCellBadge"
import { FilterCellButton } from "../../components/FilterCellButton"
import { FilterCellRoot } from "../../components/FilterCellRoot"
import { FilterRendererContext } from "../../contexts/FilterRendererContext/context"
import type { FilterDefRenderProps } from "../BaseFilterDef"
import type { ProcedureTypeFilterDef } from "./ProcedureTypeFilterDef"

type ProcedureTypeFilterCellProps = FilterDefRenderProps<ProcedureTypeFilterDef>

export const ProcedureTypeFilterCell = (props: ProcedureTypeFilterCellProps) => {
	const { filterDef, filter, filterManager } = props

	const { size } = use(FilterRendererContext)

	const procedureTypes = filterDef.procedureTypes
	const filterValue = filter.getFilterValue()
	const filterModifier = filter.getFilterModifier()

	const selectedProcedureType = useMemo(() => {
		if (filterValue === undefined || !filterDef.modifiers.equals.isFilterValueValid(filterValue)) return undefined
		return procedureTypes.find(({ uuid }) => uuid === filterValue)
	}, [filterDef.modifiers.equals, filterValue, procedureTypes])

	const selectedProcedureTypes = useMemo(() => {
		if (filterValue === undefined || !filterDef.modifiers.includes.isFilterValueValid(filterValue)) return undefined
		const set = new Set(filterValue)
		return procedureTypes.filter(({ uuid }) => set.has(uuid))
	}, [filterDef.modifiers.includes, filterValue, procedureTypes])

	const handleValueSelect = useCallback(
		(procedureType?: Stored<ProcedureType>) => {
			filter.setFilterValue(procedureType?.uuid)
		},
		[filter],
	)

	const handleValuesChange = useCallback(
		(procedureTypes: Stored<ProcedureType>[]) => {
			if (procedureTypes.length > 0) {
				filter.setFilterValue(procedureTypes.map((procedureType) => procedureType.uuid))
			} else {
				filter.setFilterValue(undefined)
			}
		},
		[filter],
	)

	const getFilterValueUi = () => {
		switch (filterModifier) {
			case "equals":
			case "notEquals": {
				return (
					<ProcedureTypeDropdownSelect
						required={false}
						value={selectedProcedureType}
						procedureTypes={procedureTypes}
						onValueChange={handleValueSelect}
						size={size}
					>
						<FilterCellButton className="shrink-1 min-w-0 grow">
							{selectedProcedureType !== undefined ? (
								<>
									<ProcedureTypeIcon procedureTypeId={selectedProcedureType.uuid} />
									<Text className="truncate">{selectedProcedureType.name}</Text>
								</>
							) : (
								"..."
							)}
						</FilterCellButton>
					</ProcedureTypeDropdownSelect>
				)
			}
			case "includes":
			case "excludes": {
				const getLabelUi = () => {
					switch ((selectedProcedureTypes ?? EMPTY_ARRAY).length) {
						case 0:
							return "..."
						case 1:
							return (
								<>
									<ProcedureTypeIcon procedureTypeId={selectedProcedureTypes![0].uuid} />
									<Text className="truncate">{selectedProcedureTypes![0].name}</Text>
								</>
							)
						default:
							return (
								<>
									<ProcedureTypeIcons procedureTypeIds={toUuidArray(selectedProcedureTypes!)} />
									{`${selectedProcedureTypes!.length} procedure types`}
								</>
							)
					}
				}

				return (
					<ProcedureTypeMultiSelect
						values={selectedProcedureTypes}
						procedureTypes={procedureTypes}
						onValuesChange={handleValuesChange}
						size={size}
					>
						<FilterCellButton className="shrink-1 min-w-0 grow">
							<span className="flex items-center gap-2 truncate">{getLabelUi()}</span>
						</FilterCellButton>
					</ProcedureTypeMultiSelect>
				)
			}
		}
	}

	return (
		<FilterCellRoot>
			<FilterCellBadge>
				<LuIcon icon={modelIconMapping.procedureType} />
				Procedure type
			</FilterCellBadge>

			<FilterModifierDropdown filterDef={filterDef} filter={filter}>
				<FilterCellButton>
					{filterDef.getFilterModifier(filterModifier).modifier.render(filterValue)}
				</FilterCellButton>
			</FilterModifierDropdown>

			{getFilterValueUi()}

			<RemoveFilterButton filter={filter} filterManager={filterManager} />
		</FilterCellRoot>
	)
}
